import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { NewsItem } from './news-items';
import { environment } from '../../../environments/environment';
import { DocumentsService } from '../documents/documents.service';

export interface CreateNewsItemDto {
  category: 'Newsletter' | 'Announcement';
  title: string;
  description?: string;
  datePosted: Date | string;
  thumbnailDocumentId?: string | null;
  contentDocumentId?: string | null;
  links?: { text: string; url: string }[];
  displayIn?: string[];
  order?: number;
}

export type UpdateNewsItemDto = Partial<CreateNewsItemDto>;

interface ApiNewsItem {
  _id: string;
  category: 'Newsletter' | 'Announcement';
  title: string;
  description?: string;
  datePosted: string;
  thumbnailDocumentId?: string;
  contentDocumentId?: string;
  links?: { text: string; url: string }[];
  displayIn?: string[];
  order?: number;
  createdBy?: string;
}

interface ApiResponse<T> {
  httpCode: string;
  message: string;
  data: T;
  timestamp: string;
}

function mapApiItem(item: ApiNewsItem): NewsItem {
  return {
    ...item,
    id: item._id,
    datePosted: new Date(item.datePosted),
  };
}

@Injectable({ providedIn: 'root' })
export class NewsItemsService {
  private apiUrl = `${environment.apiConfig.uri}/news`;

  constructor(private http: HttpClient, private documentsService: DocumentsService) {}

  getNewsItems(params?: { category?: string; displayIn?: string }): Observable<NewsItem[]> {
    let httpParams = new HttpParams();
    if (params?.category) httpParams = httpParams.set('category', params.category);
    if (params?.displayIn) httpParams = httpParams.set('displayIn', params.displayIn);
    return this.http
      .get<ApiResponse<ApiNewsItem[]>>(this.apiUrl, { params: httpParams })
      .pipe(map(r => r.data.map(mapApiItem)));
  }

  getNewsItem(id: string): Observable<NewsItem> {
    return this.http
      .get<ApiResponse<ApiNewsItem>>(`${this.apiUrl}/${id}`)
      .pipe(map(r => mapApiItem(r.data)));
  }

  createNewsItem(body: CreateNewsItemDto): Observable<NewsItem> {
    return this.http
      .post<ApiResponse<ApiNewsItem>>(this.apiUrl, body)
      .pipe(map(r => mapApiItem(r.data)));
  }

  updateNewsItem(id: string, body: UpdateNewsItemDto): Observable<NewsItem> {
    return this.http
      .put<ApiResponse<ApiNewsItem>>(`${this.apiUrl}/${id}`, body)
      .pipe(map(r => mapApiItem(r.data)));
  }

  deleteNewsItem(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  resolveDocumentUrl(documentId?: string | null): Observable<string | undefined> {
    if (!documentId) return of(undefined);
    return this.documentsService.getSignedUrl(documentId).pipe(map(r => r.url));
  }

  resolveContentDocument(documentId?: string | null): Observable<{ url: string; mimeType: string } | undefined> {
    if (!documentId) return of(undefined);
    return this.documentsService.getSignedUrl(documentId).pipe(
      map(r => ({ url: r.url, mimeType: r.mimeType ?? '' }))
    );
  }
}
